import {useMutation, useQueryClient} from '@tanstack/react-query';
import raiseFlashMessage from '@/utils/raiseFlashMessage';
import translate from '@/utils/translate';
import {Campaign} from '@/types/campaign';
import campaignUpdaters from '@/api/updaters/campaign';

export type CloneCampaignResponse = {
  data: Campaign;
};

export type CloneCampaignVariables = {
  campaignId: string;
  network?: string | null;
};

interface UseCloneCampaignOptions {
  onMutate?: (variables: CloneCampaignVariables) => void;
  onSuccess?: (
    data: CloneCampaignResponse,
    variables: CloneCampaignVariables
  ) => void;
  onError?: (variables: CloneCampaignVariables) => void;
}

function useCloneCampaign(options: UseCloneCampaignOptions = {}) {
  const queryClient = useQueryClient();

  return useMutation<CloneCampaignResponse, Error, CloneCampaignVariables>({
    mutationFn: ({campaignId, network}) =>
      campaignUpdaters.cloneCampaign(
        campaignId,
        network
      ) as unknown as Promise<CloneCampaignResponse>,

    onMutate: (variables) => {
      options.onMutate?.(variables);
    },

    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({queryKey: ['campaignsV2List']});
      raiseFlashMessage({
        status: 'success',
        message: translate('Brief cloned successfully.'),
      });
      options.onSuccess?.(data, variables);
    },

    onError: (_err, variables) => {
      raiseFlashMessage({
        status: 'error',
        message: translate('Failed to clone brief. Please try again.'),
      });
      options.onError?.(variables);
    },
  });
}

export default useCloneCampaign;
